import React from "react"
import { styled } from "@mui/material/styles"
import { css } from '@emotion/react';

import { Grid, InputLabel, MenuItem, Select, Slider } from "@mui/material"
import ThemeProviderWrapper from "./ThemeProviderWrapper"
import TagSelectFilter from "./TagSelectFilter"

const TagFiltersWrapper = styled("div")`
  width: calc(100% - 28px);
  margin: 0 auto 20px;
`

const TagFiltersHeader = styled.h5`
  margin: 35px 0;
  @media only screen and (max-width: 600px) {
    margin: 15px 0 35px 0;
    font-size: 20px;
  }
`

const StyledSlider = styled(Slider)`
  margin-top: -4.5px;
  width: 96%;
  align-self: center;
  .MuiSlider-valueLabelOpen {
    top: 53.5px;
    border: 1px solid #c0c0c0;
    background-color: white;
    color: black;
    font-family: Lato;
    line-height: 1.75;
    font-size: 12px;
    padding: 1px 4px;
    border-radius: 4px;
    ::before {
      width: 6px;
      height: 6px;
      bottom: 24px;
      border-top: 1px solid #c0c0c0;
      border-left: 1px solid #c0c0c0;
    }
  }
  .MuiSlider-thumb {
    width: 20px;
    height: 20px;
    box-shadow: none;
    &:hover,
    &:focus,
    &:active {
      box-shadow: none;
    }
  }
`

const SelectContainer = styled("div")`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const SliderInputLabel = styled(InputLabel)`
  margin-left: -5px;
`

const SFSelect = styled(Select)`
  background-color: white;
`

export default class TagFilters extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      shortestSampleDuration: 0,
      longestSampleDuration: props.longestSampleDuration,
    }
  }

  static defaultProps = {
    tempoRangeArray: [0, 200],
  }

  handleTempoSliderChange = (event, tempoRangeArray) => {
    const { onTempoSliderChange } = this.props
    onTempoSliderChange({ tempoRangeArray: tempoRangeArray })
  }

  handleTempoSliderChangeCommitted = (event, tempoRangeArray) => {
    const { onTempoSliderChangeCommitted } = this.props
    onTempoSliderChangeCommitted({ tempoRangeArray: tempoRangeArray })
  }

  handleDurationSliderChange = (event, durationRangeArray) => {
    const { onDurationSliderChange } = this.props
    onDurationSliderChange({ durationRangeArray: durationRangeArray })
  }

  handleDurationSliderChangeCommitted = (event, durationRangeArray) => {
    const { onDurationSliderChangeCommitted } = this.props
    onDurationSliderChangeCommitted({ durationRangeArray: durationRangeArray })
  }

  handleSelectChange = (event) => {
    const { onTagFilterChange } = this.props
    const { name, value } = event.target
    onTagFilterChange({ [name]: value })
  }

  render() {
    const {
      headerText,
      pageType,
      searchParam,
      longestSampleDuration,
      sortOptions,
      formattedSampleCount,
      tagOptions,
      handleSelectTagsChange,
      durationRangeArray,
      tempoRangeArray,
      selectedSortBy,
      selectedKey,
      selectedMode,
      selectedAdditionalTagOptions,
      selectedExcludeTagOptions,
    } = this.props

    const modeOptions = ["major", "minor"]
    const musicalKeys = [
      "C",
      "C#",
      "D",
      "D#",
      "E",
      "F",
      "F#",
      "G",
      "G#",
      "A",
      "A#",
      "B",
    ]

    let heightOfTagPanel = 500
    if (typeof window !== "undefined") {
      heightOfTagPanel = window.innerHeight - 370
      if (window.innerHeight < 660) {
        heightOfTagPanel = 320
      }
    }

    return (
      <ThemeProviderWrapper>
        <TagFiltersWrapper>
          <TagFiltersHeader className="flow-text">
            <strong>{formattedSampleCount}</strong>
            {pageType === "tag" &&
            <>
              {" "}
              {headerText}
              {" "}
              "<strong>{searchParam}</strong>"
            </>}
            {pageType === "category" &&
              <>{" "}samples</>
            }
            {pageType === "search" &&
              <>{" "} search results for "<strong>{searchParam}</strong>"</>
            }
          </TagFiltersHeader>
          <Grid container spacing={4} rowSpacing={1}>
            <Grid item xs={6} sm={3} md={3}>
              <Grid container justifyContent="space-between" direction="column" spacing={4}>
                <Grid item xs={12}>
                  <SelectContainer sx={{ margin: "0 5px", paddingRight: "5px" }}>
                    <SliderInputLabel>
                      Tempo (BPM)
                    </SliderInputLabel>
                    <StyledSlider
                      value={tempoRangeArray}
                      onChange={this.handleTempoSliderChange}
                      onChangeCommitted={this.handleTempoSliderChangeCommitted}
                      valueLabelDisplay="on"
                      min={0}
                      max={200}
                      aria-label={`Tempo range slider, selected values: ${tempoRangeArray[0]} and ${tempoRangeArray[1]}`}
                    />
                  </SelectContainer>
                </Grid>
                <Grid item xs={12}>
                  <SelectContainer sx={{ margin: "0 5px", paddingRight: "5px" }}>
                    <SliderInputLabel>
                      Duration (secs)
                    </SliderInputLabel>
                    <StyledSlider
                      value={durationRangeArray}
                      onChange={this.handleDurationSliderChange}
                      onChangeCommitted={this.handleDurationSliderChangeCommitted}
                      valueLabelDisplay="on"
                      step={0.1}
                      min={0}
                      max={longestSampleDuration}
                      aria-label={`Duration range slider, selected values: ${durationRangeArray[0]} and ${durationRangeArray[1]}`}
                    />
                  </SelectContainer>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={6} sm={3} md={3}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <SelectContainer>
                    <InputLabel htmlFor="key-select">Key</InputLabel>
                    <SFSelect
                      id="key-select"
                      name="key"
                      onChange={this.handleSelectChange}
                      value={selectedKey || "Any"}
                      aria-label="Key select"
                    >
                      <MenuItem value="Any">Any</MenuItem>
                      {musicalKeys.map((key) => (
                        <MenuItem value={key} key={key}>
                          {key}
                        </MenuItem>
                      ))}
                    </SFSelect>
                  </SelectContainer>
                </Grid>
                <Grid item xs={6}>
                  <SelectContainer>
                    <InputLabel htmlFor="mode-select">Mode</InputLabel>
                    <SFSelect
                      id="mode-select"
                      name="mode"
                      onChange={this.handleSelectChange}
                      value={selectedMode || "Any"}
                      aria-label="Mode select"
                    >
                      <MenuItem value="Any">Any</MenuItem>
                      {modeOptions.map((mode) => (
                        <MenuItem value={mode} key={mode}>
                          {mode}
                        </MenuItem>
                      ))}
                    </SFSelect>
                  </SelectContainer>
                </Grid>
              </Grid>
              <Grid container>
                <SelectContainer sx={{ marginTop: "6px"}}>
                  <InputLabel>Sort By</InputLabel>
                  <SFSelect
                    id="sortBy-select"
                    name="sortBy"
                    onChange={this.handleSelectChange}
                    value={selectedSortBy || "Newest"}
                    aria-label="Sort by"
                  >
                    {sortOptions.map((sortOption) => (
                      <MenuItem value={sortOption} key={sortOption}>
                        {sortOption}
                      </MenuItem>
                    ))}
                  </SFSelect>
                </SelectContainer>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <Grid container>
                <SelectContainer css={css`
                  margin-bottom: 2px;
                  @media only screen and (max-width: 600px) {
                    margin-top: 25px;
                  }
                `}>
                  <InputLabel sx={{ margin: "0 0 -2px 3px" }}>Include Tags</InputLabel>
                  <TagSelectFilter
                    tagOptions={tagOptions}
                    heightOfTagPanel={heightOfTagPanel}
                    handleSelectChange={handleSelectTagsChange}
                    name={"additional_tags"}
                    selectedOptions={selectedAdditionalTagOptions}
                  />
                </SelectContainer>
              </Grid>
              <Grid container>
                <SelectContainer sx={{ marginTop: "2px" }}>
                  <InputLabel sx={{ margin: "0 0 -2px 3px" }}>Exclude Tags</InputLabel>
                  <TagSelectFilter
                    tagOptions={tagOptions}
                    heightOfTagPanel={heightOfTagPanel}
                    handleSelectChange={handleSelectTagsChange}
                    name={"exclude_tags"}
                    selectedOptions={selectedExcludeTagOptions}
                  />
                </SelectContainer>
              </Grid>
            </Grid>
          </Grid>
        </TagFiltersWrapper>
      </ThemeProviderWrapper>
    )
  }
}
